// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// Region merge confirmation. Picks a target region to absorb the source
// into, previews the affected child counts (mirrors deletion_impact),
// then POSTs to /regions/:id/merge_into. The source region is destroyed
// server-side as part of the same transaction — the caller should reload
// its region list on success.
//
// Distinct from DeletionConfirmModal because the audit-log classification
// and operator intent differ ("권역 병합" vs "권역 삭제").

const BLOCKED_STATUSES = new Set(["closed"]);

async function jsonFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "same-origin", ...opts });
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error(body?.details?.join(", ") || body?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    if (body?.error) err.code = body.error;
    throw err;
  }
  return body;
}

export function RegionMergeModal({ source, allRegions, onClose, onMerged }) {
  useEscapeKey(onClose);
  const [targetId, setTargetId] = React.useState("");
  const [impact, setImpact] = React.useState(null);
  const [impactLoading, setImpactLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);
  // 병합은 되돌릴 수 없고 소스 권역을 삭제하므로, "병합 진행"이 곧장 실행되지 않게
  // 명시적 확인 단계를 둔다(권역 삭제 모달과 동일한 확인 게이트 철학).
  // false=대상 선택 단계, true=최종 확인 단계.
  const [confirming, setConfirming] = React.useState(false);

  // Eligible targets: not the source, not closed, not already destroyed.
  // Sorted by code so the dropdown stays consistent run-to-run.
  const candidates = React.useMemo(() =>
    (allRegions || [])
      .filter(r => r.id !== source.id && !BLOCKED_STATUSES.has(r.operation_status))
      .sort((a, b) => (a.code || "").localeCompare(b.code || "")),
    [allRegions, source.id]
  );

  // Pull the source's child counts via the existing deletion_impact path.
  // Merge moves the same children — reusing this endpoint avoids a second
  // server-side method that would have to stay in sync.
  React.useEffect(() => {
    setImpactLoading(true);
    fetch(`/api/v1/regions/${source.id}/deletion_impact`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`오류 (${r.status})`)))
      .then(j => setImpact(j.impact || {}))
      .catch(() => setImpact({}))
      .finally(() => setImpactLoading(false));
  }, [source.id]);

  const target = React.useMemo(
    () => candidates.find(r => String(r.id) === String(targetId)),
    [candidates, targetId]
  );

  // 1단계 → 2단계 전환. 실제 API 호출은 하지 않고 최종 확인 화면만 띄운다.
  const proceedToConfirm = (e) => {
    if (e) e.preventDefault();
    setErr(null);
    if (!target) return setErr("흡수할 대상 권역을 선택해주세요.");
    setConfirming(true);
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    if (!target) return setErr("흡수할 대상 권역을 선택해주세요.");
    setSubmitting(true);
    try {
      const updated = await jsonFetch(`/api/v1/regions/${source.id}/merge_into`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ target_id: target.id }),
      });
      onMerged?.(updated);
    } catch (e) {
      const msg = e.status === 401 ? "로그인이 필요합니다."
                : e.status === 403 ? "시스템 관리자 권한이 필요합니다."
                : apiErrorMessage(e);
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const childRows = React.useMemo(() => ([
    { key: "organizations", label: "유관기관",   value: impact?.organizations },
    { key: "care_managers", label: "케어매니저", value: impact?.care_managers },
    { key: "elderlies",     label: "어르신",     value: impact?.elderlies },
    { key: "cases",         label: "케이스",     value: impact?.cases },
  ]), [impact]);

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <form onSubmit={confirming ? submit : proceedToConfirm} onClick={e=>e.stopPropagation()} className="card" style={{width:560, padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12}}>
          <div>
            <div className="h3">권역 병합</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              <b style={{color:'var(--ink-2)'}}>{source.name}</b>({source.code})을(를) 다른 권역으로 흡수합니다. 이 작업은 되돌릴 수 없습니다.
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && (
          <div role="alert" style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>
            {err}
          </div>
        )}

        {!confirming ? (
        <div style={{padding:22, display:'grid', gap:16}}>
          <div>
            <div className="label">흡수할 대상 권역 *</div>
            {candidates.length === 0 ? (
              <div className="muted" style={{fontSize:12, padding:'8px 0'}}>병합 가능한 다른 권역이 없습니다.</div>
            ) : (
              <select className="select" value={targetId} onChange={e=>setTargetId(e.target.value)} required>
                <option value="">선택…</option>
                {candidates.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.code} · {r.name}{r.area_description ? ` — ${r.area_description}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <div className="label">이동될 자원</div>
            <div style={{padding:'10px 14px', background:'var(--bg-sub)', borderRadius:10}}>
              {impactLoading ? (
                <div className="muted" style={{fontSize:12}}>집계 중…</div>
              ) : (
                <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10}}>
                  {childRows.map(row => (
                    <div key={row.key} style={{textAlign:'center'}}>
                      <div className="muted" style={{fontSize:10.5, fontWeight:600}}>{row.label}</div>
                      <div className="mono" style={{fontSize:18, fontWeight:800, color:'var(--ink-1)', marginTop:2}}>
                        {row.value ?? 0}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {target && (
                <div className="muted" style={{fontSize:11, marginTop:12, paddingTop:10, borderTop:'1px solid var(--border-soft)'}}>
                  자원은 모두 <b style={{color:'var(--ink-2)'}}>{target.name}</b>으로 이동하고,
                  소스 권역의 행정구역 설명은 대상 권역에 합쳐집니다.
                </div>
              )}
            </div>
          </div>
        </div>
        ) : (
        // 2단계: 최종 확인. 소스→대상과 이동 자원·비가역성을 다시 한 번 명시해
        // 오클릭으로 병합이 실행되지 않게 막는다.
        <div style={{padding:22}}>
          <div style={{padding:'16px 18px', background:'var(--rose-50)', borderRadius:10}}>
            <div style={{fontWeight:800, fontSize:14, color:'var(--ink-1)'}}>정말 병합하시겠습니까?</div>
            <div className="muted" style={{fontSize:12.5, marginTop:8, lineHeight:1.7}}>
              <b style={{color:'var(--ink-2)'}}>{source.code} · {source.name}</b>을(를){" "}
              <b style={{color:'var(--ink-2)'}}>{target?.code} · {target?.name}</b>(으)로 흡수합니다.<br/>
              유관기관 <b style={{color:'var(--ink-2)'}}>{impact?.organizations ?? 0}</b> ·
              케어매니저 <b style={{color:'var(--ink-2)'}}>{impact?.care_managers ?? 0}</b> ·
              어르신 <b style={{color:'var(--ink-2)'}}>{impact?.elderlies ?? 0}</b> ·
              케이스 <b style={{color:'var(--ink-2)'}}>{impact?.cases ?? 0}</b>개가 이동하고,
              소스 권역 <b style={{color:'var(--ink-2)'}}>{source.name}</b>은(는) 삭제됩니다.
            </div>
            <div style={{fontSize:12.5, marginTop:10, color:'var(--rose-500)', fontWeight:700}}>
              이 작업은 되돌릴 수 없습니다.
            </div>
          </div>
        </div>
        )}

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          {!confirming ? (
            <>
              <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>취소</button>
              <button type="submit" className="btn btn-primary btn-md"
                disabled={!targetId || candidates.length === 0}
                style={{background:'var(--rose-500)'}}>
                병합 진행
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-ghost btn-md" onClick={()=>setConfirming(false)} disabled={submitting}>뒤로</button>
              <button type="submit" className="btn btn-primary btn-md"
                disabled={submitting}
                style={{background: submitting ? undefined : 'var(--rose-500)'}}>
                {submitting ? '병합 중…' : '확인하고 병합'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
